/**
 * PWA helpers — register SW, detect standalone, install prompt, local install history.
 */

import i18n from './i18n';

export type DisplayMode = 'standalone' | 'browser' | 'minimal-ui' | 'fullscreen' | 'window-controls-overlay';

export type ClientSurface = 'phone' | 'desktop';

const INSTALLS_KEY = 'acm_pwa_installs_v1';
/** Starý permanent dismiss — už nepoužíváme (jen session snooze). */
const LEGACY_DISMISS_KEY = 'acm_pwa_install_dismissed';
const SNOOZE_SESSION_KEY = 'acm_pwa_banner_snooze';

export interface PwaInstallRecord {
  /** ISO time when we first saw this client as installed / accepted install */
  at: string;
  /** phone | desktop rough surface */
  surface: ClientSurface;
  /** How it was recorded */
  source: 'standalone' | 'prompt-accepted' | 'manual';
  /** Short UA fingerprint (not full UA) */
  label: string;
}

export function isStandaloneDisplay(): boolean {
  if (typeof window === 'undefined') return false;
  const mq = window.matchMedia('(display-mode: standalone)').matches;
  // iOS Safari
  const ios =
    'standalone' in navigator &&
    (navigator as Navigator & { standalone?: boolean }).standalone === true;
  return mq || ios;
}

export function getDisplayMode(): DisplayMode {
  if (typeof window === 'undefined') return 'browser';
  if (isStandaloneDisplay()) return 'standalone';
  const modes: DisplayMode[] = [
    'fullscreen',
    'minimal-ui',
    'window-controls-overlay',
    'browser',
  ];
  for (const m of modes) {
    try {
      if (window.matchMedia(`(display-mode: ${m})`).matches) return m;
    } catch { /* */ }
  }
  return 'browser';
}

/** iPhone / iPad (včetně iPadOS desktop UA). */
export function isIosDevice(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  return (
    /iPad|iPhone|iPod/.test(ua) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  );
}

export function isIosSafari(): boolean {
  if (typeof navigator === 'undefined') return false;
  if (!isIosDevice()) return false;
  const ua = navigator.userAgent;
  const webkit = /WebKit/.test(ua);
  const other = /CriOS|FxiOS|EdgiOS|OPiOS|DuckDuckGo/i.test(ua);
  return webkit && !other;
}

export function getClientSurface(): ClientSurface {
  if (typeof window === 'undefined') return 'desktop';
  const narrow = window.matchMedia('(max-width: 768px)').matches;
  const touch = navigator.maxTouchPoints > 0;
  if (narrow || (touch && window.innerWidth < 1024)) return 'phone';
  return 'desktop';
}

function clientLabel(): string {
  if (typeof navigator === 'undefined') return 'unknown';
  const ua = navigator.userAgent;
  let browser = i18n.t('pwa.browserFallback', 'Prohlížeč');
  if (/Edg\//.test(ua)) browser = 'Edge';
  else if (/Chrome\//.test(ua) && !/Edg\//.test(ua)) browser = 'Chrome';
  else if (/Firefox\//.test(ua)) browser = 'Firefox';
  else if (/Safari\//.test(ua) && !/Chrome\//.test(ua)) browser = 'Safari';
  const os = /Android/i.test(ua)
    ? 'Android'
    : /iPhone|iPad|iPod/i.test(ua)
    ? 'iOS'
    : /Mac OS/i.test(ua)
    ? 'macOS'
    : /Windows/i.test(ua)
    ? 'Windows'
    : /Linux/i.test(ua)
    ? 'Linux'
    : 'OS';
  return `${browser} · ${os}`;
}

/** Vyčistí SW + Cache Storage a znovu načte aktuální index.html. */
export async function hardReloadUi(): Promise<void> {
  try {
    if ('serviceWorker' in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister()));
    }
  } catch { /* */ }
  try {
    if (typeof caches !== 'undefined') {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k)));
    }
  } catch { /* */ }
  const url = new URL(window.location.href);
  url.searchParams.set('_acm', String(Date.now()));
  window.location.replace(`${url.pathname}${url.search}${url.hash}`);
}

/** Register service worker (production / same-origin). */
export function registerServiceWorker(): void {
  if (typeof window === 'undefined' || !('serviceWorker' in navigator)) return;
  if (!window.isSecureContext) return;
  window.addEventListener('load', () => {
    void navigator.serviceWorker
      .register('/sw.js', { updateViaCache: 'none' })
      .then((reg) => {
        void reg.update();
        const poke = () => {
          void reg.update();
        };
        setInterval(poke, 60_000);
        document.addEventListener('visibilitychange', () => {
          if (document.visibilityState === 'visible') poke();
        });
      })
      .catch(() => {
        /* ignore — some hosts block SW */
      });
    navigator.serviceWorker.addEventListener('message', (ev) => {
      if ((ev.data as { type?: string } | null)?.type !== 'acm-sw-reload') return;
      void hardReloadUi();
    });
  });
}

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
};

let deferredPrompt: BeforeInstallPromptEvent | null = null;

export function captureInstallPrompt(): void {
  if (typeof window === 'undefined') return;
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    deferredPrompt = e as BeforeInstallPromptEvent;
    window.dispatchEvent(new CustomEvent('acm-pwa-installable'));
  });
  window.addEventListener('appinstalled', () => {
    recordPwaInstall('prompt-accepted');
    deferredPrompt = null;
    window.dispatchEvent(new CustomEvent('acm-pwa-installed'));
  });
}

export function canPromptInstall(): boolean {
  return deferredPrompt != null;
}

export async function promptInstall(): Promise<'accepted' | 'dismissed' | 'unavailable'> {
  if (!deferredPrompt) return 'unavailable';
  const ev = deferredPrompt;
  deferredPrompt = null;
  await ev.prompt();
  const { outcome } = await ev.userChoice;
  if (outcome === 'accepted') recordPwaInstall('prompt-accepted');
  return outcome;
}

export function loadPwaInstallRecords(): PwaInstallRecord[] {
  try {
    const raw = localStorage.getItem(INSTALLS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (x): x is PwaInstallRecord =>
        !!x &&
        typeof x === 'object' &&
        typeof (x as PwaInstallRecord).at === 'string' &&
        typeof (x as PwaInstallRecord).label === 'string',
    );
  } catch {
    return [];
  }
}

function savePwaInstallRecords(list: PwaInstallRecord[]): void {
  try {
    localStorage.setItem(INSTALLS_KEY, JSON.stringify(list.slice(0, 12)));
  } catch { /* */ }
}

/** Zaznamená instalaci / spuštění jako app na tomto zařízení (localStorage). */
export function recordPwaInstall(source: PwaInstallRecord['source']): void {
  const label = clientLabel();
  const surface = getClientSurface();
  const list = loadPwaInstallRecords();
  // stejným labelem nepřepisuj dokola — jen obnov čas
  const existing = list.find((r) => r.label === label && r.surface === surface);
  if (existing) {
    existing.at = new Date().toISOString();
    existing.source = source;
    savePwaInstallRecords(list);
    return;
  }
  list.unshift({
    at: new Date().toISOString(),
    surface,
    source,
    label,
  });
  savePwaInstallRecords(list);
}

/** Při otevření v standalone zapiš „tady běží jako app“. */
export function ensureStandaloneRecorded(): void {
  if (!isStandaloneDisplay()) return;
  recordPwaInstall('standalone');
}

export interface PwaStatus {
  standalone: boolean;
  displayMode: DisplayMode;
  surface: ClientSurface;
  iosDevice: boolean;
  iosSafari: boolean;
  /** true jen když Chromium umí beforeinstallprompt (ne iOS) */
  canInstall: boolean;
  clientLabel: string;
  secure: boolean;
  records: PwaInstallRecord[];
}

export function getPwaStatus(): PwaStatus {
  const iosDevice = isIosDevice();
  return {
    standalone: isStandaloneDisplay(),
    displayMode: getDisplayMode(),
    surface: getClientSurface(),
    iosDevice,
    iosSafari: isIosSafari(),
    // iOS nikdy nemá beforeinstallprompt — neriskovat falešné „Instalovat“
    canInstall: !iosDevice && canPromptInstall(),
    clientLabel: clientLabel(),
    secure: typeof window !== 'undefined' ? window.isSecureContext : false,
    records: loadPwaInstallRecords(),
  };
}

export function clearPwaInstallRecords(): void {
  try {
    localStorage.removeItem(INSTALLS_KEY);
  } catch { /* */ }
}

/** Odstraní starý permanent dismiss (křížek dřív schoval banner navždy). */
export function clearLegacyPwaDismiss(): void {
  try {
    localStorage.removeItem(LEGACY_DISMISS_KEY);
  } catch { /* */ }
}

/** Banner: skrýt jen do konce session (ne napořád). */
export function snoozePwaBannerSession(): void {
  try {
    sessionStorage.setItem(SNOOZE_SESSION_KEY, '1');
  } catch { /* */ }
}

export function isPwaBannerSnoozed(): boolean {
  try {
    return sessionStorage.getItem(SNOOZE_SESSION_KEY) === '1';
  } catch {
    return false;
  }
}

export async function promptInstallSafe(): Promise<'accepted' | 'dismissed' | 'unavailable'> {
  if (!deferredPrompt) return 'unavailable';
  return promptInstall();
}
